import React, { useCallback, useEffect, useState } from "react";
import { Button, Error, Label } from "../..";
import TreeSelect from "../../UI/TreeSelect";
import { Controller, useForm } from "react-hook-form";
import { getAxios, postAxios, putAxios } from "../../../Services/commonService";
import {
  API_CREATE_ROLES,
  API_GET_FORM,
  API_GET_FORM_BY_ID,
  API_GET_ROLES_BY_ID,
  API_UPDATE_ROLES,
} from "../../../../utils/api/ApiConstant";
import DropdownCheckboxes from "../../UI/DropdownCheckbox";
import { yupResolver } from "@hookform/resolvers/yup";
import { RolesModalSchema } from "../../../Schema/Configuration/FormSchema";

const emptyRow = (id) => ({
  id,
  formId: null,
  checkboxList: [],
  selectedPermissions: [],
  loading: false,
});

const RolesModal = ({ handleModalHide, rowData, fetchData }) => {
  const isEdit = !!rowData?.id;

  const [formData, setFormData] = useState(null);
  const [dropdownList, setDropdownList] = useState([]);
  const [loadingDropdown, setLoadingDropdown] = useState(false);
  const [permissionRows, setPermissionRows] = useState([emptyRow(Date.now())]);

  const findParentLabel = useCallback((parentId, tree) => {
    for (const node of tree) {
      if (node.value === parentId) {
        return node.label;
      }
      if (node.children && node.children.length > 0) {
        const found = findParentLabel(parentId, node.children);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      key: "",
      parentId: null,
      description: "",
      formPermissions: [],
    },
    resolver: yupResolver(RolesModalSchema),
  });

  useEffect(() => {
    const fetchForms = async () => {
      setLoadingDropdown(true);
      try {
        const response = await getAxios(API_GET_FORM);
        setDropdownList(
          response?.map((item) => ({ id: item.id, name: item.name })) || [],
        );
      } catch (err) {
        console.error("Failed to fetch forms:", err);
      } finally {
        setLoadingDropdown(false);
      }
    };
    fetchForms();
  }, []);

  useEffect(() => {
    if (!isEdit) return;

    const fetchRoleDetail = async () => {
      try {
        const response = await getAxios(`${API_GET_ROLES_BY_ID}/${rowData.id}`);
        const role = response;
        setFormData(role);

        reset({
          name: role?.name || "",
          key: role?.key || "",
          description: role?.description || "",
          parentId: role.parentId
            ? {
                label:
                  findParentLabel(role.parentId, rowData?.parentIdList || []) ??
                  String(role.parentId),
                value: role.parentId,
              }
            : null,
          formPermissions: [],
        });

        if (role?.formPermissions?.length > 0) {
          const grouped = role.formPermissions.reduce((acc, item) => {
            if (!acc[item.formId]) {
              acc[item.formId] = {
                permissionIds: [],
              };
            }
            acc[item.formId].permissionIds.push(item.permissionId);
            return acc;
          }, {});

          const rows = await Promise.all(
            Object.entries(grouped).map(async ([formId, data], index) => {
              try {
                const res = await getAxios(`${API_GET_FORM_BY_ID}/${formId}`);
                const checkboxList = (res?.permissions || []).map((p) => ({
                  id: p.permissionId,
                  label: p.permissionName,
                }));
                return {
                  id: Date.now() + index,
                  formId: Number(formId),
                  checkboxList,
                  selectedPermissions: data.permissionIds,
                  loading: false,
                };
              } catch {
                return emptyRow(Date.now() + index);
              }
            }),
          );

          setPermissionRows(rows);
          const groupedForPayload = Object.entries(grouped).map(
            ([formId, permIds]) => ({
              formId: Number(formId),
              permissions: permIds,
            }),
          );
          setValue("formPermissions", groupedForPayload);
        }
      } catch (err) {
        console.error("Failed to fetch role detail:", err);
      }
    };

    fetchRoleDetail();
  }, [isEdit, rowData?.id, findParentLabel]);

  const usedFormIds = permissionRows.map((r) => r.formId).filter(Boolean);
  const getFilteredDropdown = (rowId) => {
    const otherUsed = permissionRows
      .filter((r) => r.id !== rowId && r.formId)
      .map((r) => r.formId);
    return dropdownList.filter((opt) => !otherUsed.includes(opt.id));
  };

  useEffect(() => {
    const grouped = permissionRows
      .filter((r) => r.formId && r.selectedPermissions?.length > 0)
      .map((r) => ({
        formId: r.formId,
        permissions: [...new Set(r.selectedPermissions)],
      }));
    setValue("formPermissions", grouped);
  }, [permissionRows]);

  const handleAddRow = () => {
    setPermissionRows((prev) => [...prev, emptyRow(Date.now())]);
  };

  const handleRemoveRow = (rowId) => {
    setPermissionRows((prev) => prev.filter((r) => r.id !== rowId));
  };

  const handleDropdownChange = async (rowId, selectedFormId) => {
    if (!selectedFormId) {
      setPermissionRows((prev) =>
        prev.map((r) =>
          r.id === rowId
            ? { ...r, formId: null, checkboxList: [], selectedPermissions: [] }
            : r,
        ),
      );
      return;
    }
    setPermissionRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? {
              ...r,
              loading: true,
              formId: selectedFormId,
              selectedPermissions: [],
            }
          : r,
      ),
    );

    try {
      const res = await getAxios(`${API_GET_FORM_BY_ID}/${selectedFormId}`);
      const list = (res?.permissions || []).map((p) => ({
        id: p.permissionId,
        label: p.permissionName || `Permission ${p.permissionId}`,
      }));

      setPermissionRows((prev) =>
        prev.map((r) =>
          r.id === rowId
            ? {
                ...r,
                formId: selectedFormId,
                checkboxList: list,
                selectedPermissions: [],
                loading: false,
              }
            : r,
        ),
      );
    } catch (err) {
      console.error("Failed to fetch permissions:", err);
      setPermissionRows((prev) =>
        prev.map((r) =>
          r.id === rowId
            ? {
                ...r,
                loading: false,
                selectedPermissions: r.selectedPermissions ?? [],
              }
            : r,
        ),
      );
    }
  };

  const handleCheckboxChange = (rowId, selectedPermissionIds) => {
    setPermissionRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? { ...r, selectedPermissions: selectedPermissionIds ?? [] }
          : r,
      ),
    );
  };

  const handleFormSubmit = async (payload) => {
    const base = {
      name: payload.name,
      key: payload.key,
      parentId: payload.parentId?.value || null,
      description: payload.description,
      formPermissions: payload.formPermissions,
    };

    let res;
    if (isEdit) {
      res = await putAxios(`${API_UPDATE_ROLES}/${rowData.id}`, {
        ...base,
        isActive: formData?.isActive,
        rowVersion: formData?.rowVersion,
      });
    } else {
      res = await postAxios(API_CREATE_ROLES, base);
    }

    if (res) {
      fetchData();
      handleModalHide();
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <div className="row m-0 p-0">
        <div className="col-lg-6 col-12 mb-2">
          <Label htmlFor="name" className="form-label" isRequired>
            Name
          </Label>
          <input
            className="form-control"
            type="text"
            id="name"
            placeholder="enter name"
            {...register("name")}
          />
          {errors.name && <Error>{errors.name.message}</Error>}
        </div>

        <div className="col-md-6 m-0 p-1">
          <Label htmlFor="key" className="form-label" isRequired>
            Key
          </Label>
          <input
            className="form-control"
            type="text"
            id="key"
            placeholder="enter key"
            {...register("key")}
          />
          {errors.key && <Error>{errors.key.message}</Error>}
        </div>

        <div className="col-xl-2 col-lg-3 col-12">
          <Label htmlFor="parentId" className="form-label">
            ParentId
          </Label>
          <Controller
            name="parentId"
            control={control}
            render={({ field }) => (
              <TreeSelect
                options={rowData?.parentIdList}
                value={field.value}
                onChange={field.onChange}
                isClearable
                placeholder="Select parent..."
              />
            )}
          />
          {errors.parentId && <Error>{errors.parentId.message}</Error>}
        </div>

        <div className="col-md-6 m-0 p-1">
          <Label htmlFor="description" className="form-label">
            Description
          </Label>
          <input
            className="form-control"
            type="text"
            id="description"
            placeholder="enter description"
            {...register("description")}
          />
          {errors.description && <Error>{errors.description.message}</Error>}
        </div>

        <div className="col-12 m-0 p-1">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Label className="form-label mb-0">Form Permissions</Label>
            <Button
              type="button"
              className="btn btn_main btn-sm"
              disabled={usedFormIds.length >= dropdownList.length}
              onClick={handleAddRow}
            >
              + Add
            </Button>
          </div>

          {permissionRows.map((row, index) => (
            <div
              key={row.id}
              className="border rounded p-2 mb-2 position-relative"
            >
              {permissionRows.length > 1 && (
                <button
                  type="button"
                  className="btn btn-sm btn-danger position-absolute"
                  style={{ top: 6, right: 6 }}
                  onClick={() => handleRemoveRow(row.id)}
                >
                  ✕
                </button>
              )}

              <DropdownCheckboxes
                dropdownData={getFilteredDropdown(row.id)}
                checkboxData={row.checkboxList}
                dropdownLabel={`Form ${index + 1}`}
                dropdownValueKey="id"
                dropdownLabelKey="name"
                checkboxValueKey="id"
                checkboxLabelKey="label"
                loadingDropdown={loadingDropdown}
                loadingCheckboxes={row.loading}
                onDropdownChange={(id) => handleDropdownChange(row.id, id)}
                onCheckboxChange={(ids) => handleCheckboxChange(row.id, ids)}
                selectedFormId={row.formId}
                selectedPermissions={row.selectedPermissions}
              />
            </div>
          ))}
        </div>
      </div>

      <Button type="submit" className="btn btn_main">
        {isEdit ? "Update" : "Create"}
      </Button>
      <Button type="button" className="btn btn_main" onClick={handleModalHide}>
        Close
      </Button>
    </form>
  );
};

export default RolesModal;
